import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridActionsCellItem,
  GridRowId,
} from '@mui/x-data-grid';
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import {
  fetchAdminOrders,
  updateOrderStatus,
  clearError,
} from '../../store/slices/adminSlice';
import { Order, OrderStatus } from '../../types';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice } from '../../utils/currency';

const orderStatusOptions: { value: OrderStatus | ''; label: string; color: any }[] = [
  { value: '', label: 'All Orders', color: 'default' },
  { value: 'PENDING', label: 'Pending', color: 'warning' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'info' },
  { value: 'PROCESSING', label: 'Processing', color: 'primary' },
  { value: 'SHIPPED', label: 'Shipped', color: 'secondary' },
  { value: 'DELIVERED', label: 'Delivered', color: 'success' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'error' },
  { value: 'RETURNED', label: 'Returned', color: 'default' },
];

const Orders: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, loading, error, totalElements } = useSelector((state: RootState) => state.admin);
  const { currencySettings } = useSettings();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const params: any = { page, size: pageSize };
    if (statusFilter) params.status = statusFilter;
    dispatch(fetchAdminOrders(params) as any);
  }, [dispatch, page, pageSize, statusFilter]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleView = (id: GridRowId) => {
    navigate(`/admin/orders/${id}`);
  };

  const handleStatusChange = async (orderId: number, newStatus: OrderStatus) => {
    try {
      await dispatch(updateOrderStatus({ orderId, status: newStatus }) as any);
      toast.success('Order status updated successfully');
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const formatPriceWithSettings = (price: number | undefined | null) => {
    if (price == null) return formatPrice(0, currencySettings);
    return formatPrice(price, currencySettings);
  };

  const getStatusColor = (status: OrderStatus) => {
    const statusOption = orderStatusOptions.find(opt => opt.value === status);
    return statusOption?.color || 'default';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  const filteredOrders = (orders || []).filter(order =>
    (order.orderNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.user?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) : false) ||
    (order.guestEmail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.guestName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'orderNumber',
      headerName: 'Order #',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value ? `${params.value.firstName} ${params.value.lastName}` : params.row.guestName || 'Guest'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.value ? params.value.email : params.row.guestEmail || 'Guest Order'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Order Date',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatPriceWithSettings(params.value)}
        </Typography>
      ),
    },
    {
      field: 'items',
      headerName: 'Items',
      width: 80,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value?.length || 0}
          color="default"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <FormControl size="small" fullWidth>
          <Select
            value={params.value}
            onChange={(e) => handleStatusChange(params.row.id, e.target.value as OrderStatus)}
            size="small"
          >
            {orderStatusOptions.slice(1).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Chip
                  size="small"
                  label={option.label}
                  color={option.color}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? params.value.replace('_', ' ') : 'N/A'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Order Details">
              <ViewIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => handleView(params.id)}
        />,
      ],
    },
  ];

  const rows: GridRowsProp = filteredOrders.map((order) => ({
    ...order,
  }));

  const ordersByStatus = (orders || []).reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {} as Record<OrderStatus, number>);

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
        Order Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Orders
              </Typography>
              <Typography variant="h4">
                {totalElements}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Orders
              </Typography>
              <Typography variant="h4" color="warning.main">
                {ordersByStatus.PENDING || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Processing Orders
              </Typography>
              <Typography variant="h4" color="primary.main">
                {(ordersByStatus.CONFIRMED || 0) + (ordersByStatus.PROCESSING || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4" color="success.main">
                {formatPriceWithSettings(orders.reduce((sum, order) => sum + order.totalAmount, 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search orders, customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Filter by Status"
                >
                  {orderStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent>
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page, pageSize }) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            loading={loading}
            disableRowSelectionOnClick
            sx={{
              height: 600,
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Orders;
